import { fetchApi } from '@/common-utilities/fetch-utilities';
import { API_ENDPOINTS } from '@/common-constants/api-constants';
import type { KBArticle, KBArticleCreatePayload, KBArticleUpdatePayload, KBArticleVersion } from '@/common-types';
import type { KBArticlesResponse, KBArticleResponse, KBArticleWithDownloadResponse } from '@/types/application/models/service/kb-article-model-responses';

interface CreateArticleResponse {
  article: KBArticle;
  uploadUrl: string;
  /**
   * Headers (already prefixed with `x-amz-meta-`) that the API signed into the presigned PUT.
   * The client must echo these exactly on upload, otherwise S3 rejects the request with a
   * signature mismatch.
   */
  uploadHeaders?: Record<string, string>;
}

export async function listArticles(): Promise<KBArticle[]> {
  const response = await fetchApi<KBArticlesResponse>(API_ENDPOINTS.KB_ARTICLES);
  return response.data.articles;
}

export async function getArticle(articleId: string): Promise<KBArticle> {
  const response = await fetchApi<KBArticleResponse>(API_ENDPOINTS.KB_ARTICLE_BY_ID(articleId));
  return response.data.article;
}

export async function getArticleWithDownloadUrl(articleId: string): Promise<{ article: KBArticle; downloadUrl: string }> {
  const response = await fetchApi<KBArticleWithDownloadResponse>(API_ENDPOINTS.KB_ARTICLE_BY_ID(articleId));
  const { article, downloadUrl } = response.data;
  if (!downloadUrl) throw new Error('Download URL not available');
  return { article, downloadUrl };
}

export async function createArticle(payload: KBArticleCreatePayload, file: File): Promise<KBArticle> {
  const response = await fetchApi<CreateArticleResponse>(API_ENDPOINTS.KB_ARTICLES, {
    method: 'POST',
    body: {
      title: payload.title,
      description: payload.description ?? '',
      fileType: payload.fileType,
      ...(payload.tags !== undefined ? { tags: payload.tags } : {}),
      contentType: file.type || 'application/octet-stream',
    },
  });

  const { article, uploadUrl, uploadHeaders } = response.data;

  await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      ...(uploadHeaders ?? {}),
    },
  });

  return article;
}

export interface UpdateArticleOptions {
  /** New file bytes; server returns a presigned PUT URL for the existing S3 key. */
  replaceFile?: File;
  /**
   * Must match the article’s stored contentType (used when the presigned URL was created).
   * Callers should pass `article.contentType` or resolve it via `getArticle` if missing.
   */
  putContentType?: string;
}

export async function updateArticle(
  articleId: string,
  payload: KBArticleUpdatePayload,
  options?: UpdateArticleOptions,
): Promise<KBArticle> {
  const body: KBArticleUpdatePayload = {
    ...payload,
    ...(options?.replaceFile ? { replaceFile: true } : {}),
  };

  const response = await fetchApi<KBArticleResponse>(API_ENDPOINTS.KB_ARTICLE_BY_ID(articleId), {
    method: 'PATCH',
    body,
  });

  const { article, uploadUrl, uploadHeaders } = response.data;

  if (options?.replaceFile && uploadUrl) {
    const ct = options.putContentType || 'application/octet-stream';
    await fetch(uploadUrl, {
      method: 'PUT',
      body: options.replaceFile,
      headers: {
        'Content-Type': ct,
        ...(uploadHeaders ?? {}),
      },
    });
  }

  return article;
}

export async function deleteArticle(articleId: string): Promise<void> {
  await fetchApi(API_ENDPOINTS.KB_ARTICLE_BY_ID(articleId), {
    method: 'DELETE',
  });
}

interface ArticleVersionsResponse {
  article: KBArticle;
  versions: KBArticleVersion[];
  count: number;
}

interface ArticleVersionDownloadResponse {
  article: KBArticle;
  downloadUrl: string;
  versionId: string;
}

export async function listArticleVersions(articleId: string): Promise<{ article: KBArticle; versions: KBArticleVersion[] }> {
  const response = await fetchApi<ArticleVersionsResponse>(API_ENDPOINTS.KB_ARTICLE_VERSIONS(articleId));
  return { article: response.data.article, versions: response.data.versions };
}

export async function getArticleVersionDownloadUrl(articleId: string, versionId: string): Promise<{ downloadUrl: string; versionId: string }> {
  const response = await fetchApi<ArticleVersionDownloadResponse>(
    API_ENDPOINTS.KB_ARTICLE_VERSION(articleId, versionId),
  );
  const { downloadUrl, versionId: vid } = response.data;
  if (!downloadUrl) throw new Error('Version download URL not available');
  return { downloadUrl, versionId: vid };
}

export default {
  listArticles,
  getArticle,
  getArticleWithDownloadUrl,
  createArticle,
  updateArticle,
  deleteArticle,
  listArticleVersions,
  getArticleVersionDownloadUrl,
};
